import React, { useState, useEffect } from 'react';
import { FiX, FiDownload, FiFileText, FiCheck } from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const getBase64ImageFromURL = (url) => {
  // On ajoute un paramètre pour éviter les problèmes de cache avec les CORS
  const cacheBustedUrl = url + (url.includes('?') ? '&' : '?') + 't=' + new Date().getTime();

  return new Promise((resolve) => {
    // On utilise fetch pour récupérer l'image en tant que blob
    fetch(cacheBustedUrl, { cache: 'no-store' })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Échec du chargement de l'image: ${response.status} ${response.statusText}`);
        }
        return response.blob();
      })
      .then(blob => {
        // On utilise FileReader pour convertir le blob en chaîne Base64
        const reader = new FileReader();
        reader.onloadend = () => {
          // Le résultat est l'URL de données en Base64
          resolve(reader.result);
        };
        reader.onerror = (error) => {
          console.error('Erreur FileReader:', error);
          resolve(null);
        };
        reader.readAsDataURL(blob);
      })
      .catch(error => {
        // Ceci interceptera les erreurs réseau et les problèmes de politique CORS
        console.error("Erreur de fetch pour la conversion en Base64:", error);
        resolve(null);
      });
  });
};

const InvoiceModal = ({ isOpen, onClose, sale, user }) => {
  const [template, setTemplate] = useState('classic');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Générer l'aperçu à chaque changement de template ou de vente
  useEffect(() => {
    if (isOpen && sale) {
      const generate = async () => {
        setIsGenerating(true);
        const doc = await generatePDFDoc();
        const blob = doc.output('bloburl');
        setPreviewUrl(blob);
        setIsGenerating(false);
      };
      generate();
    }
    // Nettoyage de l'URL blob pour éviter les fuites de mémoire
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sale, template, user]);

  if (!isOpen || !sale) return null;

  const generatePDFDoc = async () => {
    const doc = new jsPDF();
    const currency = user?.currency || 'CFA';
    const shopName = user?.shopName || 'Ma Boutique';
    const address = user?.address || '';
    const phone = user?.phone || '';
    const date = new Date(sale.date).toLocaleDateString('fr-FR');
    const saleId = sale._id.substring(0, 8).toUpperCase();
    
    let logoData = null;
    if (user?.logoUrl) {
      logoData = await getBase64ImageFromURL(user.logoUrl);
    }

    const formatCurrency = (amount) => {
        return `${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} ${currency}`;
    };

    const colors = {
        primary: template === 'modern' ? [33, 150, 243] : [0, 0, 0],
        dark: [50, 50, 50],
        light: [150, 150, 150]
    };

    if (template === 'classic') {
        // --- Modèle Classique : Luxe, Serif (Style Cabinet d'avocat / Haute Couture) ---
        doc.setFont("times", "roman");
        let startY = 30;
        
        if (logoData) {
            doc.addImage(logoData, 'PNG', 95, 10, 20, 20); // Logo centré
            startY += 15;
        }
        
        // En-tête centré épuré
        doc.setFontSize(28);
        doc.setFont("times", "bold");
        doc.setTextColor(20, 20, 20);
        doc.text(shopName.toUpperCase(), 105, startY, { align: "center" });
        
        doc.setFontSize(9);
        doc.setFont("times", "normal");
        doc.setTextColor(100, 100, 100);
        doc.text(`${address} • ${phone}`, 105, startY + 8, { align: "center" });
        
        // Ligne fine élégante
        doc.setDrawColor(200, 200, 200);
        doc.line(40, startY + 15, 170, startY + 15);
        
        // Info Client et Facture
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        
        // Gauche : Client
        doc.text("POUR :", 20, startY + 35);
        doc.setFont("times", "bold");
        doc.text(sale.clientId?.name || "Client de passage", 20, startY + 42);
        doc.setFontSize(10);
        doc.setFont("times", "normal");
        if(sale.clientId?.phone) doc.text(sale.clientId.phone, 20, startY + 48);

        // Droite : Détails
        doc.setFontSize(11);
        doc.text(`FACTURE N° ${saleId}`, 190, startY + 35, { align: "right" });
        doc.setFontSize(10);
        doc.text(`Date : ${date}`, 190, startY + 42, { align: "right" });
        
        autoTable(doc, {
            startY: startY + 65,
            head: [['DESCRIPTION', 'QTÉ', 'PRIX UNIT', 'MONTANT']],
            body: sale.items.map(item => [
                item.name || item.productId?.name || 'Produit',
                item.quantity,
                formatCurrency(item.price || item.salePrice || 0),
                formatCurrency(item.quantity * (item.price || item.salePrice || 0))
            ]),
            theme: 'plain',
            headStyles: { fillColor: [255, 255, 255], textColor: 0, fontStyle: 'bold', lineWidth: { bottom: 0.5 }, lineColor: 0 },
            styles: { font: "times", fontSize: 10, cellPadding: 5, lineWidth: { bottom: 0.1 }, lineColor: 230 },
            columnStyles: { 0: { cellWidth: 'auto' }, 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        const subtotal = sale.totalAmount + (sale.discount || 0);
        
        // Total Section
        doc.setFont("times", "normal");
        doc.setFontSize(11);

        if (sale.discount > 0) {
            doc.text("Sous-total", 140, finalY);
            doc.text(formatCurrency(subtotal), 190, finalY, { align: "right" });
            
            doc.setTextColor(200, 0, 0);
            doc.text("Remise", 140, finalY + 7);
            doc.text(`-${formatCurrency(sale.discount)}`, 190, finalY + 7, { align: "right" });
            doc.setTextColor(0, 0, 0);

            doc.setDrawColor(150, 150, 150);
            doc.line(140, finalY + 12, 190, finalY + 12);

            doc.setFont("times", "bold");
            doc.setFontSize(12);
            doc.text("TOTAL", 140, finalY + 19);
            doc.setFontSize(14);
            doc.text(formatCurrency(sale.totalAmount), 190, finalY + 19, { align: "right" });
        } else {
            doc.setFont("times", "bold");
            doc.setFontSize(12);
            doc.text("TOTAL", 140, finalY);
            doc.setFontSize(14);
            doc.text(formatCurrency(sale.totalAmount), 190, finalY, { align: "right" });
        }
        
        // Footer
        doc.setFont("times", "normal");
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text("Merci de votre confiance", 105, 285, { align: "center" });

    } else if (template === 'modern') {
        // --- Modèle Moderne : SaaS Style (Stripe/Vercel) ---
        const textDark = [17, 24, 39]; // Gray-900
        const textLight = [107, 114, 128]; // Gray-500
        
        let headerX = 20;
        if (logoData) {
            doc.addImage(logoData, 'PNG', 20, 20, 12, 12);
            headerX = 38;
        }

        // Brand Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(...textDark);
        doc.text(shopName, headerX, 30);
        
        // Status Badge (Paid)
        doc.setFillColor(220, 252, 231); // Green-100
        doc.roundedRect(175, 22, 20, 8, 1, 1, 'F');
        doc.setFontSize(8);
        doc.setTextColor(22, 163, 74); // Green-600
        doc.text("PAYÉ", 185, 27, { align: "center" });

        // Grid Layout for Info
        const startY = 60;
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...textLight);
        doc.text("DE", 20, startY);
        doc.text("FACTURÉ À", 80, startY);
        doc.text("DÉTAILS", 140, startY);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);

        // Col 1 : From
        doc.text(shopName, 20, startY + 6);
        doc.setTextColor(...textLight);
        doc.text(address, 20, startY + 11);
        doc.text(phone, 20, startY + 16);

        // Col 2 : To
        doc.setTextColor(...textDark);
        doc.text(sale.clientId?.name || "Client de passage", 80, startY + 6);
        doc.setTextColor(...textLight);
        if (sale.clientId?.phone) doc.text(sale.clientId.phone, 80, startY + 11);

        // Col 3 : Details
        doc.setTextColor(...textDark);
        doc.text(`#${saleId}`, 140, startY + 6);
        doc.setTextColor(...textLight);
        doc.text(date, 140, startY + 11);

        autoTable(doc, {
            startY: startY + 35,
            head: [['Description', 'Qté', 'Prix', 'Total']],
            body: sale.items.map(item => [
                item.name || item.productId?.name || 'Produit',
                item.quantity,
                formatCurrency(item.price || item.salePrice || 0),
                formatCurrency(item.quantity * (item.price || item.salePrice || 0))
            ]),
            theme: 'plain',
            headStyles: { fillColor: [249, 250, 251], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
            styles: { font: "helvetica", fontSize: 10, cellPadding: 6, textColor: [55, 65, 81], lineColor: [229, 231, 235], lineWidth: { bottom: 0.1 } },
            columnStyles: { 0: { cellWidth: 90 }, 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' } },
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        const subtotal = sale.totalAmount + (sale.discount || 0);
        
        // Total Box
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...textLight);

        if (sale.discount > 0) {
            doc.text("Sous-total", 145, finalY + 5, { align: "right" });
            doc.text(formatCurrency(subtotal), 190, finalY + 5, { align: "right" });

            doc.text("Remise", 145, finalY + 11, { align: "right" });
            doc.setTextColor(22, 163, 74); // Green for discount
            doc.text(`-${formatCurrency(sale.discount)}`, 190, finalY + 11, { align: "right" });
            doc.setTextColor(...textLight);

            doc.setDrawColor(229, 231, 235);
            doc.line(145, finalY + 15, 190, finalY + 15);

            doc.text("Total à payer", 145, finalY + 20, { align: "right" });
            doc.setFont("helvetica", "bold");
            doc.setFontSize(20);
            doc.setTextColor(...textDark);
            doc.text(formatCurrency(sale.totalAmount), 190, finalY + 28, { align: "right" });
        } else {
            doc.text("Total à payer", 190, finalY + 5, { align: "right" });
            doc.setFont("helvetica", "bold");
            doc.setFontSize(20);
            doc.setTextColor(...textDark);
            doc.text(formatCurrency(sale.totalAmount), 190, finalY + 15, { align: "right" });
        }
        // Footer
        doc.setTextColor(...textLight);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(`Généré par Jaapand-e pour ${shopName}`, 105, 285, { align: "center" });

    } else if (template === 'minimal') {
        // --- Modèle Minimaliste : Swiss Style / Galerie d'art ---
        doc.setFont("helvetica", "normal");
        
        let headerY = 20;
        if (logoData) {
            doc.addImage(logoData, 'PNG', 20, 15, 10, 10);
            headerY = 32;
        }

        // Header Info (Small, Uppercase)
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(shopName.toUpperCase(), 20, headerY);
        doc.text(address, 20, headerY + 5);
        doc.text(phone, 20, headerY + 10);
        
        doc.text(`NO. ${saleId}`, 190, headerY, { align: "right" });
        doc.text(date, 190, headerY + 5, { align: "right" });

        // Client Section
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("CLIENT", 20, 50);
        doc.setFontSize(14);
        doc.text(sale.clientId?.name || "Client de passage", 20, 58);
        if (sale.clientId?.phone) {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(sale.clientId.phone, 20, 64);
        }

        autoTable(doc, {
            startY: 80,
            head: [['ARTICLE', 'QTÉ', 'PRIX', 'TOTAL']],
            body: sale.items.map(item => [
                item.name || item.productId?.name || 'Produit',
                item.quantity,
                formatCurrency(item.price || item.salePrice || 0),
                formatCurrency(item.quantity * (item.price || item.salePrice || 0))
            ]),
            theme: 'plain',
            headStyles: { fillColor: null, textColor: 150, fontStyle: 'normal', fontSize: 8, cellPadding: {bottom: 5} },
            styles: { font: "helvetica", fontSize: 10, cellPadding: {top: 8, bottom: 8}, lineWidth: {bottom: 0.1}, lineColor: 230 },
            columnStyles: { 0: { cellWidth: 'auto' }, 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        });

        const finalY = doc.lastAutoTable.finalY + 15;
        const subtotal = sale.totalAmount + (sale.discount || 0);
        
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);

        if (sale.discount > 0) {
            doc.text("SOUS-TOTAL", 190, finalY, { align: "right" });
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text(formatCurrency(subtotal), 190, finalY + 7, { align: "right" });

            doc.setFontSize(10);
            doc.setTextColor(150, 150, 150);
            doc.text("REMISE", 190, finalY + 14, { align: "right" });
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text(`-${formatCurrency(sale.discount)}`, 190, finalY + 21, { align: "right" });

            doc.setFontSize(10);
            doc.setTextColor(150, 150, 150);
            doc.text("TOTAL", 190, finalY + 30, { align: "right" });
            doc.setFontSize(30);
            doc.setTextColor(0, 0, 0);
            doc.text(formatCurrency(sale.totalAmount), 190, finalY + 42, { align: "right" });
        } else {
            doc.text("TOTAL", 190, finalY, { align: "right" });
            doc.setFontSize(30);
            doc.setTextColor(0, 0, 0);
            doc.text(formatCurrency(sale.totalAmount), 190, finalY + 12, { align: "right" });
        }
    }

    return doc;
  };

  const handleDownload = async () => {
    const doc = await generatePDFDoc();
    doc.save(`facture_${sale._id.substring(0, 8)}.pdf`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-0 md:p-4">
      <div className="bg-white md:rounded-xl shadow-2xl w-full h-full md:h-[90vh] md:max-w-6xl flex flex-col md:flex-row overflow-hidden">
        
        {/* Sidebar Controls */}
        <div className="w-full md:w-1/4 bg-gray-50 p-4 md:p-6 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col flex-shrink-0">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800 flex items-center"><FiFileText className="mr-2" /> Facture</h3>
            <button onClick={onClose} className="md:hidden text-gray-500 hover:text-gray-700"><FiX size={24} /></button>
          </div>
          
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wider">Modèle</p>
            <div className="flex md:flex-col gap-2 overflow-x-auto no-scrollbar md:space-y-3">
              {['classic', 'modern', 'minimal'].map(t => (
                <button 
                    key={t} 
                    onClick={() => setTemplate(t)} 
                    className={`w-full p-3 rounded-lg border-2 flex items-center justify-between transition-all ${template === t ? 'border-brand-blue bg-blue-50 text-brand-blue' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                >
                  <span className="font-medium capitalize">{t === 'classic' ? 'Classique' : t === 'modern' ? 'Moderne' : 'Minimaliste'}</span>
                  {template === t && <FiCheck />}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-auto space-y-3 hidden md:block">
            <button onClick={handleDownload} className="w-full bg-brand-blue hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors shadow-md">
                <FiDownload className="mr-2" /> Télécharger PDF
            </button>
            <button onClick={onClose} className="w-full bg-white border border-gray-300 text-gray-700 font-bold py-3 px-4 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors">
                Fermer
            </button>
          </div>
        </div>

        {/* Preview Area */}
        <div className="w-full md:w-3/4 bg-gray-200 p-2 md:p-8 flex flex-col relative flex-grow overflow-hidden">
            <button onClick={onClose} className="hidden md:block absolute top-4 right-4 text-gray-500 hover:text-gray-800 bg-white rounded-full p-2 shadow-sm z-10"><FiX size={20} /></button>
            <div className="flex-grow bg-white shadow-lg rounded-lg overflow-hidden relative h-full">
                {previewUrl && !isGenerating ? (
                    <iframe src={previewUrl} className="w-full h-full" title="Aperçu de la facture" />
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                        {isGenerating ? "Génération de la facture..." : "Chargement..."}
                    </div>
                )}
            </div>
            {/* Mobile Bottom Action */}
            <div className="md:hidden mt-2">
                <button onClick={handleDownload} className="w-full bg-brand-blue hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors shadow-md"><FiDownload className="mr-2" /> Télécharger PDF</button>
            </div>
        </div>

      </div>
    </div>
  );
};

export default InvoiceModal;